// The dashboard's machine, as the Mac app's Home card (ios/Mac/Window/HomePane.swift): one card, the
// desktop as it is now on the left (a third), and on the right the machine drawn small with its name,
// On and what it is, Restart and ⋯ on that line, then four numbers two by two (CPU, Memory, Network,
// Disk), each with its last hour under it. Restart and Change size are dialogs.

import { IconDeviceDesktop, IconDots, IconRefresh } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { conn } from '../net/connection';
import { holdConnection } from '../net/live';
import { navigate } from '../router';
import { refreshMe } from '../session';
import type { Me } from '../store';
import { ActionMenu } from '../ui/ActionMenu';
import { buttonClass } from '../ui/Button';
import { Mini } from '../ui/ds';
import { useOnScreen } from '../ui/motion';
import { PixelMeter } from '../ui/PixelMeter';
import { ChangeTier, Chart, RebootControl, fmtBytes, fmtRate, tierName, useTierChoices, useUsage } from './MachineControls';

const HOUR = 3600;
/** How often the desktop's picture is taken again while the card is on screen (the Mac's 3 s). */
const SHOT_MS = 3000;

/** The desktop as it is now, every few seconds while it's on screen; until it runs, how to start it. */
function DesktopShot({ on }: { on: boolean }) {
  const [shot, setShot] = useState<string | null>(null);
  useEffect(() => {
    if (!on) return;
    const release = holdConnection();
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = async () => {
      try {
        const s = await conn.control<{ running?: boolean }>('desktop.status');
        if (s.running) {
          const r = await conn.control<{ jpeg?: string }>('desktop.shot', { width: 880 });
          // swapped in place: a fresh picture over the last, never the well between them
          if (alive && r.jpeg) setShot(`data:image/jpeg;base64,${r.jpeg}`);
        } else if (alive) setShot(null);
      } catch {
        /* not reachable right now: the last picture (or the well) stays */
      }
      if (alive) timer = setTimeout(look, SHOT_MS);
    };
    void look();
    return () => {
      alive = false;
      clearTimeout(timer);
      release();
    };
  }, [on]);
  return (
    <a className="mh-shot" href="/app?desktop" aria-label="Open the desktop" data-testid="machine-desktop">
      {shot ? (
        <img src={shot} alt="" />
      ) : (
        <span className="mh-shot-empty">
          <IconDeviceDesktop size={24} stroke={1.5} aria-hidden="true" />
          <span>Starts when you open it</span>
        </span>
      )}
      <span className={buttonClass('secondary', 'sm', 'mh-shot-open')} aria-hidden="true">
        <IconDeviceDesktop size={16} stroke={2} aria-hidden="true" />
        Open desktop
      </span>
    </a>
  );
}

/** One of the four numbers: its label, the value with what it's of on its baseline, its last hour under. */
function Fact({ label, value, sub, testid, children }: { label: string; value: string; sub?: string; testid: string; children: React.ReactNode }) {
  return (
    <div className="mh-fact" data-testid={testid}>
      <span className="ds-pix" data-size="11">{label}</span>
      <span className="mh-fact-value">
        <strong>{value}</strong>
        {sub && <span>{sub}</span>}
      </span>
      <div className="mh-fact-chart">{children}</div>
    </div>
  );
}

/** The four numbers, two by two, from the last hour of samples. */
function Facts() {
  const { samples } = useUsage('1h');
  const s = samples ?? [];
  const last = s[s.length - 1];
  const times = s.map((x) => x.t);
  const pick = (f: (x: (typeof s)[number]) => number) => s.map(f);
  const dash = '–';
  return (
    <div className="mh-facts" data-testid="usage-card">
      <Fact label="CPU" testid="usage-cpu" value={last ? `${Math.round(last.cpu)}%` : dash} sub={last ? `of ${last.cpus} vCPU` : undefined}>
        <Chart times={times} span={HOUR} max={100} fmt={(v) => `${Math.round(v)}%`} series={[{ values: pick((x) => x.cpu), label: 'CPU', tone: 'accent' }]} />
      </Fact>
      <Fact label="Memory" testid="usage-memory" value={last ? fmtBytes(last.memUsed) : dash} sub={last ? `of ${fmtBytes(last.memTotal)}` : undefined}>
        <Chart times={times} span={HOUR} max={last?.memTotal} fmt={fmtBytes} series={[{ values: pick((x) => x.memUsed), label: 'used', tone: 'accent' }]} />
      </Fact>
      <Fact label="Network" testid="usage-network" value={last ? fmtRate(last.netRx + last.netTx) : dash}>
        <Chart times={times} span={HOUR} fmt={fmtRate} series={[{ values: pick((x) => x.netRx + x.netTx), label: 'in + out', tone: 'accent' }]} />
      </Fact>
      <Fact label="Disk" testid="usage-disk" value={last ? fmtBytes(last.diskUsed) : dash} sub={last ? `of ${fmtBytes(last.diskTotal)}` : undefined}>
        <PixelMeter share={last ? last.diskUsed / Math.max(1, last.diskTotal) : 0} label="Disk used" />
      </Fact>
    </div>
  );
}

export function MachineHome({ me }: { me: Me }) {
  const machine = me.machine!;
  const paid = me.billing.enabled;
  const ref = useRef<HTMLElement>(null);
  const seen = useOnScreen(ref);
  const { choices } = useTierChoices();
  const [dialog, setDialog] = useState<'restart' | 'size' | null>(null);
  const on = machine.status === 'running';
  const specs = [
    tierName(choices, machine.tier),
    machine.vcpu && `${machine.vcpu} vCPU`,
    machine.memoryGB && `${machine.memoryGB} GB memory`,
    machine.diskGB && `${machine.diskGB} GB disk`,
    machine.region,
  ].filter(Boolean);
  return (
    <section ref={ref} id="machine" className="card card-machine mh" aria-labelledby="machine-title" data-testid="machine-card" data-machine-id={machine.id}>
      <DesktopShot on={seen && on} />
      <div className="mh-side">
        <div className="mh-head">
          <Mini on={on} />
          <div className="mh-title">
            <div className="mh-name">
              <h2 id="machine-title" data-testid="machine-id">
                {machine.id}
              </h2>
              <span className="mh-on" data-on={on || undefined} data-testid="machine-status">
                <span className="mh-on-mark" aria-hidden="true" />
                {on ? 'on' : machine.status}
              </span>
            </div>
            <span className="mh-specs">{on ? specs.join(' · ') : 'cmx can’t reach it right now. Your sessions keep going; this page tries again on its own.'}</span>
          </div>
          <div className="mh-actions">
            <button className={buttonClass('secondary', 'md')} onClick={() => setDialog('restart')} data-testid="reboot">
              <IconRefresh size={16} stroke={2} aria-hidden="true" />
              Restart
            </button>
            {/* the rest behind ⋯: one thing to do in plain sight */}
            <ActionMenu
              className={buttonClass('ghost', 'md', 'btn-icon')}
              testid="machine-more"
              ariaLabel="More"
              label={<IconDots size={16} stroke={2} aria-hidden="true" />}
              items={[
                paid
                  ? { key: 'plan', label: 'Change plan', testid: 'change-plan', onSelect: () => navigate('/account#plan') }
                  : { key: 'size', label: 'Change size', testid: 'change-tier', onSelect: () => setDialog('size') },
              ]}
            />
          </div>
        </div>
        <Facts />
      </div>
      {dialog === 'restart' && <RebootControl machineId={machine.id} dialog={{ onClose: () => setDialog(null) }} onDone={() => void refreshMe()} />}
      {dialog === 'size' && <ChangeTier dialog={{ onClose: () => setDialog(null) }} onChanged={() => void refreshMe()} />}
    </section>
  );
}
